import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, Clock, Store } from 'lucide-react';

const WEEKDAYS = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];

export default function BarSettings() {
  const [settings, setSettings] = useState({ name: '', address: '', phone: '', break_minutes: 0, rounding_minutes: 0 });
  const [hours, setHours] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([API.get('/bar-settings'), API.get('/opening-hours')]);
      setSettings(s.data);
      const grid = Array.from({ length: 7 }, (_, i) => {
        const dayHours = h.data.filter(oh => oh.weekday === i);
        return {
          weekday: i,
          slots: dayHours.length > 0
            ? dayHours.map(oh => ({ start_time: oh.start_time, end_time: oh.end_time }))
            : [{ start_time: '', end_time: '' }]
        };
      });
      setHours(grid);
    } catch { toast.error('Error cargando configuracion'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await API.put('/bar-settings', {
        name: settings.name, address: settings.address, phone: settings.phone,
        break_minutes: parseInt(settings.break_minutes) || 0,
        rounding_minutes: parseInt(settings.rounding_minutes) || 0
      });
      toast.success('Configuracion guardada');
    } catch { toast.error('Error guardando'); }
    finally { setSaving(false); }
  };

  const handleSaveHours = async () => {
    setSaving(true);
    try {
      const items = [];
      hours.forEach(day => {
        day.slots.forEach((slot, idx) => {
          if (slot.start_time && slot.end_time) {
            items.push({ weekday: day.weekday, start_time: slot.start_time, end_time: slot.end_time, slot_index: idx });
          }
        });
      });
      await API.put('/opening-hours', items);
      toast.success('Horario de apertura guardado');
    } catch { toast.error('Error guardando horario'); }
    finally { setSaving(false); }
  };

  const updateSlot = (weekday, slotIdx, field, value) => {
    setHours(prev => prev.map(d => {
      if (d.weekday !== weekday) return d;
      return { ...d, slots: d.slots.map((s, i) => i === slotIdx ? { ...s, [field]: value } : s) };
    }));
  };

  const addSlot = (weekday) => {
    setHours(prev => prev.map(d => {
      if (d.weekday !== weekday) return d;
      if (d.slots.length >= 2) return d;
      return { ...d, slots: [...d.slots, { start_time: '', end_time: '' }] };
    }));
  };

  const removeSlot = (weekday, slotIdx) => {
    setHours(prev => prev.map(d => {
      if (d.weekday !== weekday) return d;
      return { ...d, slots: d.slots.filter((_, i) => i !== slotIdx) };
    }));
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bar-settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-zinc-50">Configuracion del Bar</h1>
        <p className="text-sm text-zinc-500">Datos generales y horario de apertura</p>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-base text-zinc-200 flex items-center gap-2">
            <Store className="w-4 h-4 text-amber-500" /> Datos del Bar
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Nombre</Label>
              <Input value={settings.name || ''} onChange={e => setSettings(s => ({ ...s, name: e.target.value }))}
                className="bg-zinc-950/50 border-white/10" data-testid="bar-name-input" aria-label="Nombre del bar" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Direccion</Label>
              <Input value={settings.address || ''} onChange={e => setSettings(s => ({ ...s, address: e.target.value }))}
                className="bg-zinc-950/50 border-white/10" aria-label="Direccion" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Telefono</Label>
              <Input value={settings.phone || ''} onChange={e => setSettings(s => ({ ...s, phone: e.target.value }))}
                className="bg-zinc-950/50 border-white/10" aria-label="Telefono" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Minutos de descanso (no computables)</Label>
              <Input type="number" value={settings.break_minutes || 0}
                onChange={e => setSettings(s => ({ ...s, break_minutes: e.target.value }))}
                className="bg-zinc-950/50 border-white/10" aria-label="Minutos descanso" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Redondeo fichaje (minutos)</Label>
              <Input type="number" value={settings.rounding_minutes || 0}
                onChange={e => setSettings(s => ({ ...s, rounding_minutes: e.target.value }))}
                className="bg-zinc-950/50 border-white/10" aria-label="Redondeo" />
            </div>
          </div>
          <Button onClick={handleSaveSettings} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="save-settings-button">
            <Save className="w-4 h-4 mr-2" /> Guardar Datos
          </Button>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-base text-zinc-200 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" /> Horario de Apertura
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {hours.map(day => (
            <div key={day.weekday} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 rounded-lg bg-zinc-900/30" data-testid={`opening-hours-day-${day.weekday}`}>
              <span className="text-sm font-medium text-zinc-300 w-24 shrink-0">{WEEKDAYS[day.weekday]}</span>
              <div className="flex-1 space-y-2">
                {day.slots.map((slot, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input type="time" value={slot.start_time} onChange={e => updateSlot(day.weekday, idx, 'start_time', e.target.value)}
                      className="bg-zinc-950/50 border-white/10 w-32" aria-label={`Inicio tramo ${idx + 1}`} />
                    <span className="text-zinc-600">-</span>
                    <Input type="time" value={slot.end_time} onChange={e => updateSlot(day.weekday, idx, 'end_time', e.target.value)}
                      className="bg-zinc-950/50 border-white/10 w-32" aria-label={`Fin tramo ${idx + 1}`} />
                    {day.slots.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeSlot(day.weekday, idx)} className="text-red-400 hover:text-red-300 text-xs px-2">Quitar</Button>
                    )}
                  </div>
                ))}
              </div>
              {day.slots.length < 2 && (
                <Button variant="ghost" size="sm" onClick={() => addSlot(day.weekday)} className="text-amber-500/70 hover:text-amber-500 text-xs">+ Tramo</Button>
              )}
            </div>
          ))}
          <Button onClick={handleSaveHours} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="save-hours-button">
            <Save className="w-4 h-4 mr-2" /> Guardar Horario
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
